import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import {
  Layers, Search, Plus, Minus, Trash2, ArrowLeft, Save, CheckCircle2, Package,
  DollarSign, Percent, Tag, GitBranch, ListPlus, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { applyActiveProductsFilter } from '../utils/productStatus';

/**
 * ComboBuilder.jsx
 * - Permite crear o editar combos.
 * - Estilo unificado con el módulo de Productos (Header -> KPIs -> Work Area).
 * - Buscador funciona como Dropdown flotante.
 * - Persistencia: Crea el producto padre (Combo) y sus relaciones (Items).
 * - IVA fijo al 21%.
 * - Botón de guardar con feedback visual de éxito (Check).
 * - NUEVO: Soporte para Combos Dinámicos (cuando la org tiene enable_dynamic_combos):
 *   en lugar de items fijos, el combo se arma con N "pasos", cada uno con su propio
 *   listado de productos elegibles y la cantidad a elegir. En el POS aparecerá
 *   un modal multi-paso para que el operador/cliente elija qué productos lleva.
 */
const ComboBuilder = ({ onNavigate }) => {
  const { profile, organization } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // product_combos.id
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Datos del Combo
  const [comboName, setComboName] = useState('');
  const [notes, setNotes] = useState('');
  const [margin, setMargin] = useState(30);
  const [finalPrice, setFinalPrice] = useState(0);

  // Buscador (Dropdown logic)
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const searchRef = useRef(null); // Para detectar click outside

  // Items seleccionados: { product_id, name, cost_price, qty }
  const [items, setItems] = useState([]);

  // === Combos Dinámicos ===
  // Lógico de empresa que habilita la feature
  const [enableDynamicCombos, setEnableDynamicCombos] = useState(false);
  // Switch local: este combo se guarda como dinámico
  const [isDynamic, setIsDynamic] = useState(false);
  // Pasos: [{ id?, step_name, quantity, options: [{product_id, name, cost_price}] }]
  const [steps, setSteps] = useState([]);
  // Buscador independiente por paso (key = idx del paso)
  const [stepSearch, setStepSearch] = useState({});
  const [stepResults, setStepResults] = useState({});
  const [stepSearching, setStepSearching] = useState({});
  const stepSearchRefs = useRef({});

  // Refs de edición
  const [comboRow, setComboRow] = useState(null);
  const [productId, setProductId] = useState(null);

  // Confirmación visual (Check en el botón)
  const [confirmPulse, setConfirmPulse] = useState(false);
  
  const pulse = () => {
    setConfirmPulse(true);
    // Mantiene el estado de "Check" por 1.5 segundos para que el usuario lo vea bien
    window.setTimeout(() => setConfirmPulse(false), 1500);
  };

  // Performance logs
  const perfStartRef = useRef(null);

  // Navegación segura
  const go = (module, entityId = null) => {
    try {
      if (typeof onNavigate === 'function') return onNavigate(module, entityId);
      if (entityId) return navigate(`/sistema/${module}/${entityId}`);
      return navigate(`/sistema/${module}`);
    } catch (e) {
      console.error('Error navegando:', e);
      toast.error('No se pudo navegar. Revisá Sistema.jsx / rutas.');
    }
  };

  // Logs de uso
  const logUsage = async (button_clicked) => {
    try {
      if (!profile?.organization_id) return;
      await supabase.from('usage_logs').insert([{
        organization_id: profile.organization_id,
        user_id: profile?.id || null,
        user_role: profile?.role || null,
        module_id: 'combo-builder',
        button_clicked,
        time_spent_seconds: 0
      }]);
    } catch (e) { /* best-effort */ }
  };

  const logPerformanceStart = () => {
    try { perfStartRef.current = Date.now(); } catch { /* best-effort */ }
  };

  const logPerformanceEnd = async (action = 'view_module') => {
    try {
      const start = perfStartRef.current;
      if (!profile?.organization_id || !start) return;
      const duration = Math.max(0, Math.round((Date.now() - start) / 1000));
      await supabase.from('performance_logs').insert([{
        user_id: profile?.id || null,
        organization_id: profile.organization_id,
        module_id: 'combo-builder',
        duration_seconds: duration,
        action
      }]);
    } catch (e) { /* best-effort */ }
    finally { perfStartRef.current = null; }
  };

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  };

  // Cálculos automáticos
  // - Combo clásico: suma de cost_price * qty
  // - Combo dinámico: estimamos costo de "elección más cara" por cada paso, multiplicado
  //   por la cantidad del paso. Es solo orientativo (la rentabilidad real depende de la
  //   selección que haga el cliente en el POS, y se loggea por venta).
  const costTotal = useMemo(() => {
    if (isDynamic) {
      return steps.reduce((acc, st) => {
        const maxOption = (st.options || []).reduce((mx, o) => Math.max(mx, Number(o.cost_price || 0)), 0);
        return acc + maxOption * Number(st.quantity || 1);
      }, 0);
    }
    return items.reduce((acc, it) => acc + (Number(it.cost_price || 0) * Number(it.qty || 0)), 0);
  }, [items, isDynamic, steps]);

  const suggestedPrice = useMemo(() => {
    const base = Number(costTotal || 0);
    const m = Number(margin || 0);
    const sug = base * (1 + (m / 100));
    return Math.round(sug * 100) / 100;
  }, [costTotal, margin]);

  // Si no es edición y no hay precio final manual, usar el sugerido
  useEffect(() => {
    if (!isEdit && finalPrice === 0) {
      setFinalPrice(suggestedPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedPrice]);

  // --- Lógica de Categoría ---
  const ensureComboCategoryId = async () => {
    const orgId = profile?.organization_id;
    if (!orgId) return null;

    // Buscar categoría "Combo" (case insensitive)
    const { data: existing, error: e1 } = await supabase
      .from('product_categories')
      .select('id, name')
      .eq('organization_id', orgId)
      .ilike('name', 'combo')
      .limit(1);

    if (e1) throw e1;
    if (existing && existing.length) return existing[0].id;

    // Si no existe, crearla
    const { data: created, error: e2 } = await supabase
      .from('product_categories')
      .insert([{ organization_id: orgId, name: 'Combo', is_active: true }])
      .select('id')
      .single();

    if (e2) throw e2;
    return created.id;
  };

  // enable_dynamic_combos viene del organization del AuthContext (un fetch por sesión).
  useEffect(() => {
    if (!organization) return;
    setEnableDynamicCombos(Boolean(organization?.enable_dynamic_combos));
  }, [organization?.enable_dynamic_combos]);

  // --- Cargar datos en edición ---
  const loadEdit = async () => {
    if (!isEdit) return;
    const orgId = profile?.organization_id;
    if (!orgId) return;

    setLoading(true);
    logPerformanceStart();
    try {
      // 1. Obtener datos del combo
      const { data: combo, error: e1 } = await supabase
        .from('product_combos')
        .select('id, product_id, suggested_margin, suggested_price, final_price, notes, is_active')
        .eq('id', id)
        .eq('organization_id', orgId)
        .single();

      if (e1) throw e1;

      setComboRow(combo);
      setProductId(combo.product_id);
      setMargin(Number(combo.suggested_margin || 30));
      setFinalPrice(Number(combo.final_price || 0));
      setNotes(combo.notes || '');

      // 2. Obtener nombre del producto padre + flag de dinámico
      const { data: prod, error: e2 } = await supabase
        .from('products')
        .select('id, name, is_dynamic_combo')
        .eq('id', combo.product_id)
        .eq('organization_id', orgId)
        .single();

      if (e2) throw e2;
      setComboName(prod?.name || '');
      setIsDynamic(Boolean(prod?.is_dynamic_combo));

      // 3. Obtener items (componentes)
      const { data: itemsRows, error: e3 } = await supabase
        .from('product_combo_items')
        .select('product_id, quantity')
        .eq('combo_id', combo.id)
        .eq('organization_id', orgId);

      if (e3) throw e3;

      // 4. Enriquecer items con nombres y costos actuales
      const pids = (itemsRows || []).map(r => r.product_id).filter(Boolean);
      let pMap = {};
      if (pids.length) {
        const { data: prods, error: e4 } = await supabase
          .from('products')
          .select('id, name, cost_price')
          .in('id', pids)
          .eq('organization_id', orgId);

        if (e4) throw e4;
        pMap = (prods || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
      }

      const merged = (itemsRows || []).map(r => ({
        product_id: r.product_id,
        name: pMap[r.product_id]?.name || 'Producto Eliminado',
        cost_price: Number(pMap[r.product_id]?.cost_price || 0),
        qty: Number(r.quantity || 1),
      }));

      setItems(merged);

      // 5. Si es dinámico, cargar pasos y opciones
      if (prod?.is_dynamic_combo) {
        const { data: stepRows, error: e5 } = await supabase
          .from('product_combo_steps')
          .select('id, step_order, step_name, quantity')
          .eq('combo_id', combo.id)
          .eq('organization_id', orgId)
          .order('step_order', { ascending: true });

        if (e5) throw e5;

        const stepIds = (stepRows || []).map(s => s.id);
        let optsByStep = {};
        let optProductsMap = {};

        if (stepIds.length) {
          const { data: optRows, error: e6 } = await supabase
            .from('product_combo_step_options')
            .select('id, step_id, product_id')
            .in('step_id', stepIds)
            .eq('organization_id', orgId);
          if (e6) throw e6;

          const optProdIds = [...new Set((optRows || []).map(o => o.product_id))];
          if (optProdIds.length) {
            const { data: optProds, error: e7 } = await supabase
              .from('products')
              .select('id, name, cost_price')
              .in('id', optProdIds)
              .eq('organization_id', orgId);
            if (e7) throw e7;
            optProductsMap = (optProds || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
          }

          (optRows || []).forEach(o => {
            (optsByStep[o.step_id] = optsByStep[o.step_id] || []).push({
              product_id: o.product_id,
              name: optProductsMap[o.product_id]?.name || 'Producto Eliminado',
              cost_price: Number(optProductsMap[o.product_id]?.cost_price || 0),
            });
          });
        }

        const stepsLoaded = (stepRows || []).map(s => ({
          id: s.id,
          step_name: s.step_name || '',
          quantity: Number(s.quantity || 1),
          options: optsByStep[s.id] || [],
        }));
        setSteps(stepsLoaded);
      }

      await logPerformanceEnd('load_module');
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el combo.");
      go('combos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.organization_id) {
      if (isEdit) loadEdit();
      else setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, id]);

  // --- Buscador / Dropdown ---
  const doSearch = async (value) => {
    const orgId = profile?.organization_id;
    if (!orgId) return;

    const s = value.trim();
    if (!s) {
      setResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await applyActiveProductsFilter(
        supabase
        .from('products')
        .select('id, name, cost_price, product_type, active, archived_at, brand')
        .eq('organization_id', orgId)
        .is('archived_at', null)
      )
        .not('product_type', 'eq', 'Combo') // No permitir combos dentro de combos
        .ilike('name', `%${s}%`)
        .limit(15);

      if (error) throw error;
      setResults(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Error buscando productos.");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, profile?.organization_id]);

  // Click outside para cerrar el dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Gestión de Items ---
  const addProduct = (p) => {
    setItems((prev) => {
      const idx = prev.findIndex(x => x.product_id === p.id);
      if (idx >= 0) {
        // Si ya existe, aumentar cantidad
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: Number(copy[idx].qty || 1) + 1 };
        return copy;
      }
      // Si es nuevo
      return [...prev, { product_id: p.id, name: p.name, cost_price: Number(p.cost_price || 0), qty: 1 }];
    });
    setResults([]); // Cerrar dropdown al seleccionar
    setSearch(''); // Limpiar input
    logUsage('agregar_item_combo');
  };

  const inc = (pid) => setItems(prev => prev.map(it => it.product_id === pid ? { ...it, qty: Number(it.qty || 0) + 1 } : it));
  const dec = (pid) => setItems(prev => prev.map(it => it.product_id === pid ? { ...it, qty: Math.max(1, Number(it.qty || 1) - 1) } : it));
  const remove = (pid) => setItems(prev => prev.filter(it => it.product_id !== pid));

  // === Gestión de pasos (combo dinámico) ===
  const addStep = () => {
    setSteps(prev => [
      ...prev,
      { step_name: `Paso ${prev.length + 1}`, quantity: 1, options: [] }
    ]);
  };
  const removeStep = (idx) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  };
  const moveStep = (idx, dir) => {
    setSteps(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };
  const updateStepField = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addStepOption = (idx, prod) => {
    setSteps(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      if ((s.options || []).some(o => o.product_id === prod.id)) return s;
      return {
        ...s,
        options: [...(s.options || []), {
          product_id: prod.id,
          name: prod.name,
          cost_price: Number(prod.cost_price || 0)
        }]
      };
    }));
    setStepResults(r => ({ ...r, [idx]: [] }));
    setStepSearch(r => ({ ...r, [idx]: '' }));
    logUsage('agregar_opcion_paso_combo');
  };
  const removeStepOption = (idx, productId) => {
    setSteps(prev => prev.map((s, i) =>
      i === idx ? { ...s, options: s.options.filter(o => o.product_id !== productId) } : s
    ));
  };

  // Buscador por paso (debounced)
  useEffect(() => {
    const timers = Object.entries(stepSearch).map(([idx, value]) => {
      return setTimeout(async () => {
        const orgId = profile?.organization_id;
        if (!orgId) return;
        const s = (value || '').trim();
        if (!s) {
          setStepResults(r => ({ ...r, [idx]: [] }));
          return;
        }
        setStepSearching(r => ({ ...r, [idx]: true }));
        try {
          const { data, error } = await applyActiveProductsFilter(
            supabase
            .from('products')
            .select('id, name, cost_price, product_type, active, archived_at, brand')
            .eq('organization_id', orgId)
            .is('archived_at', null)
          )
            .not('product_type', 'eq', 'Combo')
            .ilike('name', `%${s}%`)
            .limit(15);
          if (error) throw error;
          setStepResults(r => ({ ...r, [idx]: data || [] }));
        } catch (e) {
          console.error(e);
        } finally {
          setStepSearching(r => ({ ...r, [idx]: false }));
        }
      }, 300);
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, [stepSearch, profile?.organization_id]);

  // Click outside para cerrar dropdowns por paso
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.entries(stepSearchRefs.current).forEach(([idx, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setStepResults(r => ({ ...r, [idx]: [] }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Guardar Combo ---
  const saveCombo = async () => {
    const orgId = profile?.organization_id;
    if (!orgId) return;

    if (!comboName.trim()) return toast.error("El nombre del combo es obligatorio.");

    // Validaciones según el modo
    if (isDynamic) {
      if (steps.length === 0) return toast.error("Agregá al menos 1 paso al combo dinámico.");
      const stepInvalido = steps.find(s => !String(s.step_name || '').trim());
      if (stepInvalido) return toast.error("Cada paso necesita un nombre (ej: Tarta, Bebida).");
      const stepSinOpciones = steps.find(s => (s.options || []).length === 0);
      if (stepSinOpciones) return toast.error(`El paso "${stepSinOpciones.step_name}" no tiene productos elegibles.`);
      const stepCantInvalida = steps.find(s => Number(s.quantity || 0) < 1);
      if (stepCantInvalida) return toast.error(`Cantidad inválida en el paso "${stepCantInvalida.step_name}".`);
    } else {
      if (items.length === 0) return toast.error("Sumá al menos 1 producto al combo.");
    }

    setSaving(true);
    const toastId = toast.loading("Guardando combo...");
    logPerformanceStart();

    try {
      const comboCategoryId = await ensureComboCategoryId();

      // 1) Crear/Actualizar PRODUCT (La entidad vendible)
      let prodId = productId;
      
      const productData = {
        name: comboName.trim(),
        description: (notes || '').trim() || null,
        product_type: 'Combo', // Importante para la lógica de descuento de stock en ventas
        is_dynamic_combo: Boolean(isDynamic), // Marca el combo como dinámico para que el POS abra el modal
        category_id: comboCategoryId,
        cost_price: Number(costTotal || 0),
        target_margin: Number(margin || 0),
        active: true,
        is_active: true,
        archived_at: null,
        iva_percentage: 21, // REQUERIMIENTO: IVA fijo al 21%
        price_usd: 0,
        sell_price: Number(finalPrice || 0),
        auto_update_price: false
      };

      if (!isEdit) {
        const { data: createdProd, error: e1 } = await supabase
          .from('products')
          .insert([{
            ...productData,
            organization_id: orgId,
          }])
          .select('id')
          .single();

        if (e1) throw e1;
        prodId = createdProd.id;
        setProductId(prodId);
      } else {
        const { error: e2 } = await supabase
          .from('products')
          .update(productData)
          .eq('id', prodId)
          .eq('organization_id', orgId);

        if (e2) throw e2;
      }

      // 2) Actualizar la tabla auxiliar product_combos
      const comboPayload = {
        organization_id: orgId,
        product_id: prodId,
        suggested_margin: Number(margin || 0),
        suggested_price: Number(suggestedPrice || 0),
        final_price: Number(finalPrice || 0),
        notes: (notes || '').trim() || null,
        is_active: true,
        archived_at: null,
      };

      let comboId = isEdit ? comboRow?.id : null;

      if (!isEdit) {
        const { data: createdCombo, error: e3 } = await supabase
          .from('product_combos')
          .insert([comboPayload])
          .select('id')
          .single();

        if (e3) throw e3;
        comboId = createdCombo.id;
      } else {
        const { error: e4 } = await supabase
          .from('product_combos')
          .update(comboPayload)
          .eq('id', comboId)
          .eq('organization_id', orgId);

        if (e4) throw e4;
      }

      // 3) Reemplazar Items / Pasos según el modo
      if (isDynamic) {
        // Limpiar items fijos (por si antes era combo clásico)
        const { error: eDelItems } = await supabase
          .from('product_combo_items')
          .delete()
          .eq('combo_id', comboId)
          .eq('organization_id', orgId);
        if (eDelItems) throw eDelItems;

        // Limpiar steps anteriores (cascadea las options por FK)
        const { error: eDelSteps } = await supabase
          .from('product_combo_steps')
          .delete()
          .eq('combo_id', comboId)
          .eq('organization_id', orgId);
        if (eDelSteps) throw eDelSteps;

        // Insertar pasos
        const stepsPayload = steps.map((s, idx) => ({
          organization_id: orgId,
          combo_id: comboId,
          step_order: idx + 1,
          step_name: String(s.step_name || `Paso ${idx + 1}`).trim(),
          quantity: Math.max(1, Number(s.quantity || 1)),
        }));

        const { data: createdSteps, error: eInsSteps } = await supabase
          .from('product_combo_steps')
          .insert(stepsPayload)
          .select('id, step_order');
        if (eInsSteps) throw eInsSteps;

        // Insertar opciones (mapeando step_order -> id)
        const stepIdByOrder = (createdSteps || []).reduce((acc, s) => { acc[s.step_order] = s.id; return acc; }, {});
        const optionsPayload = [];
        steps.forEach((s, idx) => {
          const stId = stepIdByOrder[idx + 1];
          (s.options || []).forEach(opt => {
            optionsPayload.push({
              organization_id: orgId,
              step_id: stId,
              product_id: opt.product_id,
            });
          });
        });

        if (optionsPayload.length) {
          const { error: eInsOpts } = await supabase
            .from('product_combo_step_options')
            .insert(optionsPayload);
          if (eInsOpts) throw eInsOpts;
        }
      } else {
        // Limpiar pasos previos (por si antes era dinámico y ahora se cambia a clásico)
        const { error: eDelSteps } = await supabase
          .from('product_combo_steps')
          .delete()
          .eq('combo_id', comboId)
          .eq('organization_id', orgId);
        if (eDelSteps) throw eDelSteps;

        const { error: e5 } = await supabase
          .from('product_combo_items')
          .delete()
          .eq('combo_id', comboId)
          .eq('organization_id', orgId);

        if (e5) throw e5;

        const itemsToInsert = items.map(it => ({
          organization_id: orgId,
          combo_id: comboId,
          product_id: it.product_id,
          quantity: Number(it.qty || 1),
        }));

        const { error: e6 } = await supabase
          .from('product_combo_items')
          .insert(itemsToInsert);

        if (e6) throw e6;
      }

      // 4) Actualizar o Crear Variante Default (para el POS)
      const { data: existingVar, error: e7 } = await supabase
        .from('product_variants')
        .select('id')
        .eq('organization_id', orgId)
        .eq('product_id', prodId)
        .limit(1);

      if (e7) throw e7;

      const variantData = {
        price: Number(finalPrice || 0),
        cost: Number(costTotal || 0),
        cost_price: Number(costTotal || 0),
        stock_quantity: 0, 
        min_stock: 0,
      };

      if (existingVar && existingVar.length) {
        const { error: e8 } = await supabase
          .from('product_variants')
          .update(variantData)
          .eq('id', existingVar[0].id)
          .eq('organization_id', orgId);

        if (e8) throw e8;
      } else {
        const { error: e9 } = await supabase
          .from('product_variants')
          .insert([{
            ...variantData,
            organization_id: orgId,
            product_id: prodId,
            sku: null,
            attributes: null,
          }]);

        if (e9) throw e9;
      }

      await logPerformanceEnd('save_combo');
      toast.success("Combo guardado exitosamente", { id: toastId });
      
      // Activar el check visual en el botón
      pulse();
      logUsage('guardar_combo');
      
      // Opcional: Navegar después de un breve delay
      // setTimeout(() => go('combos'), 1000);

    } catch (e) {
      console.error(e);
      toast.error("Error al guardar el combo", { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#F2F9FF] p-8">
        <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
          <Layers className="text-slate-300 animate-pulse" size={18} />
        </div>
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Combo…</p>
      </div>
    );
  }

  // --- RENDER ---
  return (
    <div className="w-full h-full p-5 md:p-6 animate-nova-in flex flex-col gap-4">
      
      {/* 1. HEADER + ACTIONS (Top) */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-lg hover:rotate-3 transition-all duration-300">
            <Layers size={22} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight uppercase">
              {isEdit ? 'Editar Combo' : 'Nuevo Combo'}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">
              Armado de packs y pricing.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => { logUsage('volver_combos'); go('combos'); }}
            className="px-5 py-3 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" /> 
            <span>Volver</span>
          </button>

          {/* BOTÓN GUARDAR MEJORADO */}
          <button
            disabled={saving || confirmPulse}
            onClick={saveCombo}
            className={`
              relative px-6 py-3 rounded-2xl shadow-lg transition-all duration-300 disabled:opacity-80 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 min-w-[160px]
              ${confirmPulse 
                ? 'bg-emerald-500 text-white scale-105' 
                : 'bg-[#3FF2AA] text-[#0B2A36] hover:shadow-[#3FF2AA]/40 hover:-translate-y-0.5'
              }
            `}
          >
            {confirmPulse ? (
              // Estado: GUARDADO (Check)
              <div className="flex items-center gap-2 animate-in fade-in zoom-in duration-300">
                <CheckCircle2 size={18} strokeWidth={3} /> 
                <span>¡GUARDADO!</span>
              </div>
            ) : (
              // Estado: NORMAL / GUARDANDO
              <div className="flex items-center gap-2">
                <Save size={16} /> 
                <span>{saving ? 'Guardando...' : 'Guardar Combo'}</span>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* 2.0 SWITCH MODO DINÁMICO (solo si la org lo habilitó) */}
      {enableDynamicCombos && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${isDynamic ? 'bg-[#0B2A36] text-[#3FF2AA]' : 'bg-slate-50 text-slate-400 border border-slate-100'}`}>
              <GitBranch size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-black text-slate-700 uppercase tracking-tight">
                Combo Dinámico
              </div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                Definí pasos y opciones — el operador elige los productos en el POS.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsDynamic(v => !v)}
            className={`w-12 h-6 rounded-full relative transition-all shadow-inner ${isDynamic ? 'bg-[#3FF2AA]' : 'bg-slate-300'}`}
            aria-label={isDynamic ? 'Desactivar modo dinámico' : 'Activar modo dinámico'}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${isDynamic ? 'left-7' : 'left-1'}`} />
          </button>
        </div>
      )}

      {/* 2. KPI STRIP (Totales en tiempo real) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        
        {/* KPI: Costo Total */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center gap-4 relative overflow-hidden">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 z-10">
            <DollarSign size={18} />
          </div>
          <div className="z-10">
            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
              {isDynamic ? 'Costo Máximo Estimado' : 'Costo Total'}
            </div>
            <div className="text-xl font-black text-slate-700">{fmtMoney(costTotal)}</div>
            {isDynamic && (
              <div className="text-[8px] font-bold uppercase tracking-widest text-amber-500 mt-0.5">
                Worst-case (opción más cara × cantidad)
              </div>
            )}
          </div>
          {/* Decorative Blob */}
          <div className="absolute -right-2 -bottom-2 w-16 h-16 bg-slate-50 rounded-full opacity-50 pointer-events-none" />
        </div>

        {/* KPI: Margen */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center gap-4 relative overflow-hidden group focus-within:ring-2 focus-within:ring-[#51A7FD]/20">
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-500 z-10">
            <Percent size={18} />
          </div>
          <div className="z-10 flex-1">
            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Margen Sugerido (%)</div>
            <input 
              type="number"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              className="w-full text-xl font-black text-slate-700 bg-transparent outline-none placeholder-slate-200"
              placeholder="0"
            />
          </div>
        </div>

        {/* KPI: Sugerido */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center gap-4 relative overflow-hidden">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500 z-10">
            <Tag size={18} />
          </div>
          <div className="z-10">
            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Precio Sugerido</div>
            <div className="text-xl font-black text-slate-400 decoration-slate-300 decoration-1">
              {fmtMoney(suggestedPrice)}
            </div>
          </div>
        </div>

        {/* KPI: Final (Editable) */}
        <div className="bg-[#0B2A36] rounded-2xl border border-[#0B2A36] p-4 shadow-lg flex items-center gap-4 relative overflow-hidden group focus-within:ring-2 focus-within:ring-[#3FF2AA]/50">
          <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/5 flex items-center justify-center text-[#3FF2AA] z-10">
            <DollarSign size={18} />
          </div>
          <div className="z-10 flex-1">
            <div className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-0.5">Precio Final (POS)</div>
            <input 
              type="number"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              className="w-full text-2xl font-black text-white bg-transparent outline-none placeholder-white/20"
              placeholder="0.00"
            />
          </div>
          <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-[8px] bg-[#3FF2AA] text-[#0B2A36] px-2 py-0.5 rounded font-bold uppercase">Editable</span>
          </div>
        </div>
      </div>

      {/* 3. MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full min-h-0">
        
        {/* COLUMNA IZQUIERDA: Buscador y Configuración Básica */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          
          {/* Card: Buscador de Productos (Combobox style) */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-visible z-30" ref={searchRef}>
            <div className="bg-black text-white px-5 py-3 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Search size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black uppercase tracking-widest">Agregar Productos</span>
              </div>
            </div>
            
            <div className="p-4 relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => { if(search) setSearching(true); }}
                  className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/20 focus:border-[#51A7FD] shadow-inner uppercase transition-all"
                  placeholder="BUSCAR PRODUCTO PARA EL COMBO..."
                />
              </div>

              {/* DROPDOWN RESULTS (Floating) */}
              {(results.length > 0 || searching) && (
                <div className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white border border-slate-100 rounded-xl shadow-xl max-h-[300px] overflow-y-auto z-50 animate-nova-in">
                  {searching && results.length === 0 ? (
                    <div className="p-4 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      Buscando...
                    </div>
                  ) : results.length > 0 ? (
                    <div className="p-2 space-y-1">
                      {results.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => addProduct(p)}
                          className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-all flex items-center justify-between gap-3 group border border-transparent hover:border-slate-100"
                        >
                          <div className="min-w-0">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-800 truncate flex items-center gap-1.5 flex-wrap">
                              <span className="truncate">{p.name}</span>
                              {p.brand && (
                                <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                  {p.brand}
                                </span>
                              )}
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-slate-500">
                              Costo: {fmtMoney(p.cost_price || 0)}
                            </div>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-[#51A7FD]/10 flex items-center justify-center group-hover:bg-[#51A7FD] transition-colors">
                            <Plus size={14} className="text-[#51A7FD] group-hover:text-white" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      Sin resultados
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Card: Datos Básicos */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex-1">
            <div className="bg-black text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black uppercase tracking-widest">Detalles del Combo</span>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Nombre del Combo</label>
                <input
                  value={comboName}
                  onChange={(e) => setComboName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                  placeholder="EJ: SUPER COMBO FAMILIAR"
                />
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Observaciones / Descripción</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase resize-none"
                  placeholder="EJ: INCLUYE PAPAS Y GASEOSA MEDIANA..."
                />
              </div>

              <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 flex items-start gap-2">
                <div className="mt-0.5 min-w-[16px]">
                  <Layers size={14} className="text-amber-500" />
                </div>
                <p className="text-[9px] text-amber-700/80 font-medium leading-relaxed">
                  Este combo se guardará con un <strong>IVA del 21%</strong>. Al venderse, el sistema descontará el stock de los productos individuales listados.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: Listado de Items o Pasos según modo */}
        <div className="lg:col-span-7 h-full flex flex-col">
         {isDynamic ? (
           <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
             <div className="bg-black text-white px-5 py-3 flex items-center justify-between">
               <div className="flex items-center gap-2">
                 <GitBranch size={16} className="text-[#3FF2AA]" />
                 <span className="text-[10px] font-black uppercase tracking-widest">Pasos del Combo Dinámico</span>
               </div>
               <button
                 type="button"
                 onClick={addStep}
                 className="px-3 py-1.5 rounded-lg bg-[#3FF2AA] text-[#0B2A36] text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:scale-105 transition-transform"
               >
                 <ListPlus size={12} /> Nuevo Paso
               </button>
             </div>

             <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-3">
               {steps.length === 0 ? (
                 <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-3 border-2 border-dashed border-slate-100 rounded-xl p-8">
                   <GitBranch size={40} strokeWidth={1.5} />
                   <div className="text-center">
                     <p className="text-[11px] font-black uppercase tracking-widest mb-1">Sin pasos definidos</p>
                     <p className="text-[10px] font-medium">Tocá "Nuevo Paso" para empezar (ej: "Tarta", "Bebida").</p>
                   </div>
                 </div>
               ) : steps.map((st, idx) => (
                 <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-visible">
                   {/* Header del paso */}
                   <div className="px-4 py-3 flex items-center gap-3 bg-white rounded-t-xl border-b border-slate-100">
                     <span className="w-7 h-7 rounded-lg bg-[#0B2A36] text-[#3FF2AA] flex items-center justify-center text-[11px] font-black">
                       {idx + 1}
                     </span>
                     <input
                       value={st.step_name}
                       onChange={(e) => updateStepField(idx, 'step_name', e.target.value)}
                       placeholder={`Nombre del paso (ej: TARTA)`}
                       className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-[11px] font-bold uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/20"
                     />
                     <div className="flex items-center gap-1 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100">
                       <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">Cant.</span>
                       <input
                         type="number"
                         min={1}
                         value={st.quantity}
                         onChange={(e) => updateStepField(idx, 'quantity', Math.max(1, Number(e.target.value || 1)))}
                         className="w-12 text-center text-[12px] font-black text-slate-700 bg-transparent outline-none"
                       />
                     </div>
                     <div className="flex items-center gap-1">
                       <button
                         type="button"
                         onClick={() => moveStep(idx, -1)}
                         disabled={idx === 0}
                         className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-30 flex items-center justify-center"
                         title="Subir"
                       >
                         <ChevronUp size={14} />
                       </button>
                       <button
                         type="button"
                         onClick={() => moveStep(idx, 1)}
                         disabled={idx === steps.length - 1}
                         className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-30 flex items-center justify-center"
                         title="Bajar"
                       >
                         <ChevronDown size={14} />
                       </button>
                       <button
                         type="button"
                         onClick={() => removeStep(idx)}
                         className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-100 hover:bg-rose-50 transition-all flex items-center justify-center"
                         title="Quitar paso"
                       >
                         <Trash2 size={13} />
                       </button>
                     </div>
                   </div>

                   {/* Body del paso: buscador + opciones */}
                   <div className="p-3 space-y-2">
                     <div className="relative" ref={(el) => { stepSearchRefs.current[idx] = el; }}>
                       <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                       <input
                         value={stepSearch[idx] || ''}
                         onChange={(e) => setStepSearch(s => ({ ...s, [idx]: e.target.value }))}
                         placeholder="BUSCAR PRODUCTO ELEGIBLE..."
                         className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-[10px] font-bold uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/20"
                       />
                       {((stepResults[idx] || []).length > 0 || stepSearching[idx]) && (
                         <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border border-slate-100 rounded-xl shadow-xl max-h-[240px] overflow-y-auto z-40">
                           {stepSearching[idx] && (stepResults[idx] || []).length === 0 ? (
                             <div className="p-3 text-center text-[9px] text-slate-400 font-bold uppercase tracking-widest">Buscando...</div>
                           ) : (stepResults[idx] || []).length > 0 ? (
                             <div className="p-1.5 space-y-1">
                               {stepResults[idx].map(p => (
                                 <button
                                   key={p.id}
                                   type="button"
                                   onClick={() => addStepOption(idx, p)}
                                   className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-all flex items-center justify-between gap-2 group"
                                 >
                                   <div className="min-w-0">
                                     <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 truncate flex items-center gap-1.5 flex-wrap">
                                       <span className="truncate">{p.name}</span>
                                       {p.brand && (
                                         <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-wide">
                                           {p.brand}
                                         </span>
                                       )}
                                     </div>
                                     <div className="text-[8px] font-bold uppercase tracking-widest text-slate-400">
                                       Costo: {fmtMoney(p.cost_price || 0)}
                                     </div>
                                   </div>
                                   <Plus size={14} className="text-[#51A7FD]" />
                                 </button>
                               ))}
                             </div>
                           ) : (
                             <div className="p-3 text-center text-[9px] text-slate-400 font-bold uppercase tracking-widest">Sin resultados</div>
                           )}
                         </div>
                       )}
                     </div>

                     {/* Lista de opciones del paso */}
                     {(st.options || []).length === 0 ? (
                       <div className="text-center py-4 text-[9px] font-bold uppercase tracking-widest text-slate-300 border border-dashed border-slate-200 rounded-lg">
                         Sin productos elegibles aún
                       </div>
                     ) : (
                       <div className="flex flex-wrap gap-1.5">
                         {st.options.map(opt => (
                           <div
                             key={opt.product_id}
                             className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 flex items-center gap-2 text-[10px] font-bold uppercase tracking-tight text-slate-700 group"
                           >
                             <span className="truncate max-w-[160px]">{opt.name}</span>
                             <span className="text-[8px] font-bold text-slate-400">
                               {fmtMoney(opt.cost_price)}
                             </span>
                             <button
                               type="button"
                               onClick={() => removeStepOption(idx, opt.product_id)}
                               className="text-slate-300 hover:text-rose-500 transition-colors"
                               title="Quitar"
                             >
                               <Trash2 size={11} />
                             </button>
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                 </div>
               ))}
             </div>
           </div>
         ) : (
           <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="bg-black text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black uppercase tracking-widest">Composición del Combo</span>
              </div>
              <span className="px-2 py-1 rounded bg-white/10 text-[9px] font-bold uppercase tracking-widest text-white/90">
                {items.length} Items
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-3 border-2 border-dashed border-slate-100 rounded-xl p-8">
                  <Package size={40} strokeWidth={1.5} />
                  <div className="text-center">
                    <p className="text-[11px] font-black uppercase tracking-widest mb-1">El combo está vacío</p>
                    <p className="text-[10px] font-medium">Usá el buscador de la izquierda para agregar productos.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map(it => (
                    <div
                      key={it.product_id}
                      className="group px-4 py-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 hover:bg-slate-100/50 transition-all flex items-center justify-between gap-4"
                    >
                      {/* Info Item */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#51A7FD]" />
                          <div className="text-[11px] font-black uppercase tracking-widest text-slate-800 truncate">
                            {it.name}
                          </div>
                        </div>
                        <div className="pl-3.5 mt-1 flex items-center gap-3">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 bg-white px-1.5 py-0.5 rounded border border-slate-100">
                            Unit: {fmtMoney(it.cost_price)}
                          </span>
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#51A7FD]">
                            Subtotal: {fmtMoney(Number(it.cost_price) * Number(it.qty))}
                          </span>
                        </div>
                      </div>

                      {/* Controles Cantidad */}
                      <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                        <button
                          onClick={() => dec(it.product_id)}
                          className="w-8 h-8 rounded-lg hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors flex items-center justify-center"
                          title="Restar"
                        >
                          <Minus size={14} />
                        </button>

                        <div className="w-10 text-center text-[12px] font-black text-slate-800">
                          {it.qty}
                        </div>

                        <button
                          onClick={() => inc(it.product_id)}
                          className="w-8 h-8 rounded-lg hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors flex items-center justify-center"
                          title="Sumar"
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Eliminar */}
                      <button
                        onClick={() => remove(it.product_id)}
                        className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-300 hover:text-rose-500 hover:border-rose-100 hover:bg-rose-50 transition-all flex items-center justify-center shadow-sm"
                        title="Quitar del combo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
           </div>
         )}
        </div>

      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { bg: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
};

export default ComboBuilder;